<script setup lang="ts" generic="T">
    // @ts-nocheck
    import BaseCalendar from "@/components/calendars/Calendar/BaseCalendar.vue";
    import CalendarFilter from "@/components/calendars/Filter/CalendarFilter.vue";
    import { CALENDAR_VIEWS, SPARRAD_TID } from "@/components/calendars/helpers";
    import CalendarToolbar from "@/components/calendars/Toolbar/CalendarToolbar.vue";
    import { useStore } from "@/store";
    import { AllowFunc, CalendarApi, EventDropArg, EventInput, EventSourceInput } from "@fullcalendar/core";
    import { EventImpl } from "@fullcalendar/core/internal";
    import { EventResizeDoneArg } from "@fullcalendar/interaction/index.js";
    import { ResourceSourceInput } from "@fullcalendar/resource";
    import { useElementVisibility } from "@vueuse/core";
    import cloneDeep from "lodash/cloneDeep";
    import { unitOfTime } from "moment/moment";
    import emitter from "tiny-emitter/instance";
    import { computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";

    const store = useStore();

    // Hårdkodad typning som inte är exporterad, pga kan inte ha externa typer innan Vue3
    interface KCalendarProps<T> {
        fetchData: (props?: {
            // Valt datum av användaren
            currentDate: Date;
            // Start och slut på datum som täcker in valt datum (om det är mer än 1 dag)
            range: { start: Date; end: Date };
            // Fullcalendar view type
            viewType: string;
            /*
                Ska en ny hämtning från BE göras, oavsett om det redan finns i store?
                Det som finns i store kommer fortfarande att användas vid rendering, men i bakgrunden kommer ny hämtning
                göras och lagras i store och visas upp för användaren
             */
            forceRefresh?: boolean;
            /*
                Finns det redan existerande data hämtad? Kan användas för att t.ex inte visa ladndingsikon och göra
                hämtning av ny data i bakgrunden
             */
            hasCache?: boolean;
            /*
                Funktion som kan anropas för att tillåta ändring av subset innan hämtning från BE är klar.
                Kan användas för att visa låta användaren se det som är i store direkt istället för att vänta på
                laddningsanimation och hämtning.
                Returnerar om nya subsetet har innehåll.
                Kan användas för att avgöra om laddningsikon ska visas eller inte
             */
            setSubsetName?: (props: { name: string }) => {
                hasCache?: boolean;
            };
        }) => Promise<Map<string, T> | { total: number; nextPage: number; subsetName: string }>;
        fetchSparradeTiderData?: (props?: {
            // Valt datum av användaren
            currentDate: Date;
            // Start och slut på datum som täcker in valt datum (om det är mer än 1 dag)
            range: { start: Date; end: Date };
            /*
                Ska en ny hämtning från BE göras, oavsett om det redan finns i store?
                Det som finns i store kommer fortfarande att användas vid rendering, men i bakgrunden kommer ny hämtning
                göras och lagras i store och visas upp för användaren
             */
            forceRefresh?: boolean;
            /*
                Finns det redan existerande data hämtad? Kan användas för att t.ex inte visa ladndingsikon och göra
                hämtning av ny data i bakgrunden
             */
            hasCache?: boolean;
            /*
                Funktion som kan anropas för att tillåta ändring av subset innan hämtning från BE är klar.
                Kan användas för att visa låta användaren se det som är i store direkt istället för att vänta på
                laddningsanimation och hämtning.
                Returnerar om nya subsetet har innehåll.
                Kan användas för att avgöra om laddningsikon ska visas eller inte
             */
            setSubsetName?: (props: { name: string }) => {
                hasCache?: boolean;
            };
        }) => Promise<Map<string, T> | { total: number; nextPage: number; subsetName: string }>;
        eventConverter: (props: { value: T }) => Partial<EventInput>;
        sparradTidEventConverter: (props: { value: T }) => Partial<EventInput>;
        // Schema för KForm. Används CalendarFilter
        filterSchema?: unknown;
        // Startvyn för Fullcalendar
        initialView?: keyof typeof CALENDAR_VIEWS;
        // Resurser till Fullcalendar
        resources?: ResourceSourceInput;
        // Funktion som anropas för att se om eventet kan släppas över en specifik del av Fullcalendar
        eventAllow?: AllowFunc;
        // Ska duration kunna redigeras av användaren på events?
        eventDurationEditable?: boolean;
        // Ska start kunna redigeras av användaren på events?
        eventStartEditable?: boolean;
        /*
            Namn på variabel som ska användas för att avgöra vilken färg events ska ha
            (t.ex "gender" kommer hämta gender från extendedProps och använda det värdet för att avgöra färg)
         */
        defaultColorKey?: string;
        // Content som ska renderas övre vänstre hörnet i resursvyer
        resourceAreaHeaderContent?: string;
        /*
         Mängden dagar/veckor/år som framåt/bakåt-knapparna i navigationen ska hoppa,
         istället för Fullcalendars inbyggda mängd
         */
        navNextOverride?: {
            amount: number;
            unit: unitOfTime.DurationConstructor;
        };
        /*
         Mängden dagar/veckor/år som framåt/bakåt-knapparna i navigationen ska hoppa,
         istället för Fullcalendars inbyggda mängd
         */
        navPrevOverride?: {
            amount: number;
            unit: unitOfTime.DurationConstructor;
        };
        // Ska veckonummer visas?
        showWeekNumbers?: boolean;
        // Ska helger visas och användas i Fullcalendar?
        weekends?: boolean;
        // Dagar att dölja. Sätt weekends: false om du bara vill dölja helger
        hiddenDays?: number[];
        // Namn på variabel och värde som ska ändras på event som dras in utifrån kalendern
        dropAreaSet?: {
            name: string;
            value: unknown;
        };
        // Id på containern i CalendarDropArea som tar emot släppta events från kalendern
        dropAreaId?: string;
        /*
         Funktion som anropas när event utifrån släpps i kalendern. Ska returnera data som sen används av bland annat
            Fullcalendar för att rendera events
         */
        onExternalDrop?: (props: { event: EventImpl; currentData: T }) => Promise<T>;
        /*
            Funktion som anropas när ett event släpps inom kalendern
         */
        onEventDrop?: (props: EventDropArg) => Promise<void>;
        // Path till subset i store module (t.ex "slutenvardsbesok/slutenvardsbesokSubset")
        storePath?: string;
        // Path till subset för spärrade tider i store module (t.ex "slutenvardsbesok/sparradeTiderSubset")
        sparradeTiderStorePath?: string;
        // Tab id används för att kunna targetta rätt tab vid refresh
        tabId?: string | number;
        filterTriggers?: Array<{
            fieldName: string; // Namn på fältet
            dependentOn: string; // Namn på fältet som ska lyssnas på
            action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                value?: unknown;
                disabled?: boolean;
                setAsInitial?: boolean;
            }; // Funktion som tar emot båda fältens värde och returnerar nytt värde och/eller om fältet ska bli disabled
        }>;
        /*
            Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
            key id som ska kollas mot och value index som ska användas vid hämtning av färg
            T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
         */
        colorKeyIndices?: { [key: string]: Map<string, number> };
        /*
            Getter för hämtning
         */
        defaultFilterGetter?: (() => { [key: string]: unknown }) | (() => Promise<{ [key: string]: unknown }>);
        dataConverter?: (props: { items: Map<string | number, T> }) => Map<string | number, T>;
        onEventResize?: (props: EventResizeDoneArg) => void;
    }

    const emit = defineEmits<{
        (
            event: "externalDrop",
            payload: {
                event: EventSourceInput;
            }
        ): void;
        (
            event: "eventResize",
            payload: {
                arg: EventResizeDoneArg;
            }
        ): void;
        (
            event: "panelAction",
            payload: {
                action: string;
                data: any;
            }
        ): void;
    }>();

    const props = withDefaults(defineProps<KCalendarProps<T>>(), {
        initialView: "week",
        weekends: false,
        defaultFilterGetter: () => ({}),
    });

    const {
        fetchData,
        fetchSparradeTiderData,
        eventConverter,
        defaultColorKey,
        initialView,
        hiddenDays,
        weekends,
        onExternalDrop,
        onEventDrop,
        storePath,
        sparradeTiderStorePath,
        tabId,
        defaultFilterGetter,
        sparradTidEventConverter,
        dataConverter,
        onEventResize,
    } = toRefs(props);

    const colorKey = ref(defaultColorKey?.value);
    /*
        Intern lagring av data. Används bara om fetchData returnerar en Map och store inte används.
        Du vill inte hämta något från den här ref:en. Det du är ute efter är variabeln computedData
     */
    const data = ref<Map<string, T>>(new Map());
    // Key för subset att lyssna på
    const subsetName = ref<string | null>(null);
    /*
        Intern lagring av data för spärrade tider. Används bara om fetchSparradeTiderData returnerar en Map och store inte används.
        Du vill inte hämta något från den här ref:en. Det du är ute efter är variabeln computedSparradeTiderData
     */
    const sparradeTiderData = ref<Map<string, T>>(new Map());
    // Key för subset att lyssna på för spärrade tider
    const sparradeTiderSubsetName = ref<string | null>(null);
    const kCalendarRef = ref();
    // Antingen hämtning från store eller interna lagringen (data)
    const computedData = computed(() => {
        // Hämtar data från store
        if (subsetName.value && storePath?.value) {
            const items = store.getters[storePath.value]({
                subsetName: subsetName.value,
                toArray: false,
                limit: 0,
                offset: 0,
            });

            if (dataConverter) {
                const result = dataConverter.value({ items });
                return result;
            } else {
                return items;
            }
        }

        // Eller det som sats direkt via fetchNewData()
        return data.value;
    });
    // Antingen hämtning från store eller interna lagringen
    const computedSparradeTiderData = computed(() => {
        // Hämtar data från store
        if (sparradeTiderSubsetName.value && sparradeTiderStorePath?.value) {
            return store.getters[sparradeTiderStorePath.value]({
                subsetName: sparradeTiderSubsetName.value,
                toArray: false,
                limit: 0,
                offset: 0,
            });
        }

        // Eller det som sats direkt via fetchNewData()
        return sparradeTiderData.value;
    });
    const filter = ref<{ [key: string]: unknown }>();
    const filterRef = ref();
    const calendarRef = ref();
    const calendarApi = ref<CalendarApi | undefined>();
    const events = computed(() => {
        const convertedEvents = [...computedData.value.values()].map((value) => eventConverter.value({ value }));
        const convertedSparradeEvents = sparradTidEventConverter?.value
            ? [...computedSparradeTiderData.value.values()].map((value) => {
                  const convertedEvent = sparradTidEventConverter.value({ value });

                  return {
                      ...convertedEvent,
                      id: `${SPARRAD_TID}${convertedEvent.id}`,
                  };
              })
            : [];
        return [...convertedEvents, ...convertedSparradeEvents];
    });
    /**
     * Filtrerad samling av events. Skickas in i Fullcalendar
     */
    const filteredEvents = computed(() => {
        if (!filter?.value || !Object.keys(filter.value).length) {
            return events.value;
        }

        const filterEntries = Object.entries(filter.value);

        return events.value.filter((event) => {
            return filterEntries.every(([filterKey, filterValue]) => {
                // TODO: Behövs en speciallösning för att filtrera anhörigbesök enligt dess slutenvårdsbesök. Anhörigbesöken har en
                // koppling till sitt slutenvårdsbesök via slutenvardsbesokId, men det kan utökas så att filtervärdena går att
                // hitta via anhörigbesök-eventet. Lösningen behöver dock vara generell för att kunna användas i KCalendar (t.ex.
                // parentFilterValues eller dylikt på child-events som ska filtreras).

                if (event.id?.startsWith(SPARRAD_TID) || event.extendedProps?.noFilter) {
                    return true;
                }

                const eventValue = event[filterKey] ?? event.extendedProps?.[filterKey];

                if (Array.isArray(filterValue)) {
                    // Om arrayen är tom eller om arrayen innehåller värdet
                    return !filterValue.length || filterValue.includes(eventValue);
                }

                if (typeof filterValue === "boolean") {
                    return !!eventValue === filterValue;
                }

                return eventValue === filterValue;
            });
        });
    });

    const viewType = ref(initialView);

    /*
        Behövs för att uppdatera storleken på Fullcalendar om den omrenderats i bakgrunden (inne i dold tab).
        Annars renderas Fullcalendar med fel höjd och bredd
     */
    const isVisible = useElementVisibility(kCalendarRef);

    const filterDataGetter = async () => {
        if (defaultFilterGetter?.value && (!filter?.value || !Object.keys(filter.value).length)) {
            return defaultFilterGetter?.value?.();
        }

        return cloneDeep(filter?.value);
    };

    /**
     * När filtrets formulär submittas
     * @param filterData
     */
    const onFilterSubmit = (filterData: { [key: string]: unknown; colorKey: string }) => {
        const { colorKey: newColorKey, ...data } = filterData;
        filter.value = data;
        colorKey.value = newColorKey;
    };

    /**
     * När filtret öppnas/stängs
     */
    const onFilterClick = () => {
        filterRef?.value?.toggleVisibility();
    };

    /**
     * Vid förändring av datum i Fullcalendarvia t.ex CalendarToolbar
     */
    const onDateChange = async (args: {
        currentDate: Date;
        viewApi: {
            activeStart: Date;
            activeEnd: Date;
            type: string;
        };
    }) => {
        await fetchNewData(args);
    };

    /**
     * Anropar inskickad fetchData och sätter resultatet för att använda antingen store eller intern lagring, beroende
     * på vad som returnerats av fetchData
     * @param currentDate
     * @param viewApi
     * @param forceRefresh
     */
    const fetchNewData = async ({
        currentDate,
        viewApi,
        forceRefresh,
    }: {
        currentDate: Date;
        viewApi: {
            activeStart: Date;
            activeEnd: Date;
            type: string;
        };
        forceRefresh?: boolean;
    }) => {
        try {
            const result = await fetchData.value({
                currentDate,
                forceRefresh,
                viewType: viewApi.type,
                range: { start: viewApi.activeStart, end: viewApi.activeEnd },
                setSubsetName: ({ name }) => {
                    subsetName.value = name;

                    return {
                        hasCache: computedData?.value?.size,
                    };
                },
            });

            if (result instanceof Map) {
                data.value = result;
            } else {
                subsetName.value = result.subsetName;
            }

            if (fetchSparradeTiderData?.value) {
                const result = await fetchSparradeTiderData.value({
                    currentDate,
                    forceRefresh,
                    range: { start: viewApi.activeStart, end: viewApi.activeEnd },
                    setSubsetName: ({ name }) => {
                        sparradeTiderSubsetName.value = name;

                        return {
                            hasCache: computedData?.value?.size,
                        };
                    },
                });

                if (result instanceof Map) {
                    sparradeTiderData.value = result;
                } else {
                    sparradeTiderSubsetName.value = result.subsetName;
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * När event utifrån släpps på kalendern
     * @param event
     * @param revert
     * @param success
     */
    const externalDrop = async ({
        event,
        revert,
        success,
    }: {
        event: EventImpl;
        revert: () => void;
        success: () => void;
    }) => {
        const newData = await onExternalDrop?.value?.({ event, currentData: computedData.value.get(event.id) as T });

        if (newData) {
            if (!storePath?.value) {
                const modifiedData = new Map(computedData.value);

                modifiedData.set((newData as T & { id: string }).id, newData);

                data.value = modifiedData;
            }

            success();
        } else {
            revert();
        }
    };

    /**
     * När event flyttas inom kalendern
     */
    const eventDrop = async (arg: EventDropArg) => {
        const newData = await onEventDrop?.value?.(arg);

        if (newData) {
            const modifiedData = new Map(data.value);

            modifiedData.set((newData as T & { id: string }).id, newData);

            data.value = modifiedData;
        } else {
            arg.revert();
        }
    };

    const eventResize = (arg: EventResizeDoneArg) => {
        onEventResize?.value?.(arg);
    };

    const panelAction = (payload: { action: string; data: any }) => {
        emit("panelAction", payload);
    };

    onMounted(async () => {
        emitter.on(`refreshTab-${tabId?.value}`, async () => {
            if (calendarApi?.value) {
                await fetchNewData({
                    forceRefresh: true,
                    currentDate: calendarApi.value.currentData.currentDate,
                    viewApi: calendarApi.value.view,
                });
            }
        });

        /*
            Behövs för att uppdatera storleken på Fullcalendar om den omrenderats i bakgrunden (inne i dold tab).
            Annars renderas Fullcalendar med fel höjd och bredd
         */
        watch(isVisible, (newValue, oldValue) => {
            if (newValue && !oldValue) {
                calendarApi?.value?.updateSize();
            }
        });

        try {
            calendarApi.value = calendarRef.value?.getApi();
        } catch (error) {
            console.error(error);
        }
    });

    onUnmounted(() => {
        emitter.off(`refreshTab-${tabId?.value}`);
    });
</script>

<template>
    <div class="k-calendar" ref="kCalendarRef">
        <slot
            name="top"
            :events="filteredEvents"
            :colorKey="colorKey"
            :data="computedData"
            :viewType="viewType"
            :colorKeyIndices="colorKeyIndices"
        />
        <CalendarFilter
            v-if="filterSchema || $slots.filter"
            ref="filterRef"
            :schema="filterSchema"
            :initialValuesGetter="filterDataGetter"
            @submit="onFilterSubmit"
            :triggers="filterTriggers"
        >
            <template #default>
                <!-- Rendering av innehåll i filtret, inom KForm -->
                <slot name="filter" />
            </template>
        </CalendarFilter>
        <CalendarToolbar
            :onFilterClick="onFilterClick"
            :calendarApi="calendarApi"
            @dateChange="onDateChange"
            @viewChange="onDateChange"
            :viewType="viewType"
            :navNextOverride="navNextOverride"
            :navPrevOverride="navPrevOverride"
            :weekends="weekends"
            :hiddenDays="hiddenDays"
        >
            <template #customActions>
                <!-- Fler knappar/annat som ska renderas i toolbar -->
                <slot name="customActions" />
            </template>
        </CalendarToolbar>
        <!-- Annat som ska renderas mellan toolbar och kalender (t.ex CalendarDropArea) -->
        <slot
            name="extra"
            :events="filteredEvents"
            :colorKey="colorKey"
            :data="computedData"
            :viewType="viewType"
            :colorKeyIndices="colorKeyIndices"
        />
        <!-- TODO: Ska onEventClick och onEventDragStop vara med här? -->
        <BaseCalendar
            ref="calendarRef"
            :events="filteredEvents"
            :initialView="initialView"
            :resources="resources"
            :eventAllow="eventAllow"
            :eventDurationEditable="eventDurationEditable"
            :eventStartEditable="eventStartEditable"
            :data="computedData"
            :sparradeTiderData="computedSparradeTiderData"
            :colorKey="colorKey"
            :colorKeyIndices="colorKeyIndices"
            :resourceAreaHeaderContent="resourceAreaHeaderContent"
            :filter="filter"
            :showWeekNumbers="showWeekNumbers"
            :weekends="weekends"
            :hiddenDays="hiddenDays"
            :dropAreaSet="dropAreaSet"
            :dropAreaId="dropAreaId"
            @externalDrop="externalDrop"
            @eventDrop="eventDrop"
            @panelAction="panelAction"
            @eventResize="onEventResize"
        />
    </div>
</template>

<style scoped lang="scss">
    .k-calendar {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        min-height: 100%;
        flex-grow: 1;
        padding-bottom: 2rem;
    }
</style>
